import { ApolloQueryResult } from '@apollo/client';
import { useFetchEQLFields } from '@modules/common/hooks';
import {
  ExecuteEqlQuery,
  GetRecordsResponseViewFragment,
  SortBy,
  useExecuteEqlLazyQuery,
} from 'generated/graphql';
import { useCallback, useMemo, useRef } from 'react';
import { AndOfErrors } from 'v2/core/errors/errors';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import {
  AndStatuses,
  CreateSkipWithStatus,
  IsLoading,
  QueryStatus,
} from 'v2/core/repository/edge-server/types';
import { mapToEQL } from 'v2/core/repository/eql/mappers';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { useGetDefaultQueryContainer } from 'v2/core/services/notebook';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { useGetUser } from 'v2/core/services/user';
import { useGetFeedbacksFromResponse } from './useGetFeedbacksFromResponse';

const DEFAULT_SORT_BY: SortBy = {
  timestamp: true,
  isAsc: false,
};

interface GetFeedbackListInput {
  queryContainer?: QueryContainer;
  sortBy?: SortBy;
  shouldSkip?: boolean;
}

export const useGetFeedbackList = ({
  queryContainer,
  sortBy,
  shouldSkip = false,
}: GetFeedbackListInput) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const defaultQueryContainer = useGetDefaultQueryContainer();

  const eqlProps = useMemo(
    () => ({
      expressionType: ExpressionType.Records,
      whereClause: queryContainer
        ? queryContainer.WhereClause
        : defaultQueryContainer.WhereClause,
      dateRange: queryContainer
        ? queryContainer.DateRange
        : defaultQueryContainer.DateRange,
      sortBy: sortBy ?? DEFAULT_SORT_BY,
    }),
    [
      queryContainer,
      sortBy,
      defaultQueryContainer.DateRange,
      defaultQueryContainer.WhereClause,
    ],
  );

  const {
    data: sourceDetailsMap,
    status: sStatus,
    error: sError,
  } = useGetSourceDetailsMap();

  const { fields: eqlFields, isLoading: isLoadingEqlFields } =
    useFetchEQLFields();

  const eql = useMemo(
    () =>
      sourceDetailsMap
        ? mapToEQL({
            ...eqlProps,
            sourceDetailsMap,
            eqlFields,
          })
        : undefined,
    [eqlProps, eqlFields, sourceDetailsMap],
  );

  const skip =
    CreateSkipWithStatus(!sourceDetailsMap || !eql, sStatus) || shouldSkip;

  const [
    lazyFetchRecords,
    {
      data,
      loading,
      error: eError,
      called,
      fetchMore: fetchMoreQuery,
      refetch,
      client,
    },
  ] = useExecuteEqlLazyQuery({
    variables: {
      tenantID,
      expression: eql ?? '',
    },
    fetchPolicy: 'network-only', // Can't use "no-cache" because if a bug in Apollo, which causes double firing of the query
    nextFetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const getRecords = data?.executeEQL?.result as GetRecordsResponseViewFragment;

  const cursorFromResponse = getRecords?.cursor;

  const cursorRef = useRef<string | null>(cursorFromResponse ?? '');

  if (!cursorRef.current) {
    cursorRef.current = cursorFromResponse ?? '';
  }

  const eStatus = apolloStatusToQueryStatus(loading, called, skip, eError);
  const eqlFieldsStatus = apolloStatusToQueryStatus(isLoadingEqlFields, true);

  const status = AndStatuses([sStatus, eStatus, eqlFieldsStatus]);
  const error = AndOfErrors([sError, eError]);

  const { feedbackRecords, hits } = useGetFeedbacksFromResponse(getRecords);

  const fetchRecords = useCallback(async () => {
    if (skip) {
      return;
    }
    return lazyFetchRecords();
  }, [lazyFetchRecords, skip]);

  const fetchMore: () => Promise<
    ApolloQueryResult<ExecuteEqlQuery | undefined> | undefined
  > = useCallback(async () => {
    if (!cursorRef.current) {
      return;
    }

    const resp = await fetchMoreQuery({
      variables: {
        cursor: cursorRef.current,
      },
    });

    const recordsData = resp?.data?.executeEQL
      ?.result as GetRecordsResponseViewFragment;

    cursorRef.current = recordsData?.cursor ?? '';

    return resp;
  }, [fetchMoreQuery]);

  return {
    fetchRecords,
    hits,
    feedbacks:
      status === QueryStatus.SUCCESS_COMPLETED ? feedbackRecords : undefined,
    error,
    fetchMore,
    status,
    refetch,
    called,
    client,
    isLoadingMore: IsLoading(status), // backwards compatibility
  };
};
